import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, TextField, Checkbox, FormControlLabel, Button, Typography, Tooltip, MenuItem } from '@mui/material';
import { RootState } from '../../globalStore/store';
import { setPackaging } from '../../reducers/product/productSlice';
import { ControlPoint, HelpOutline } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Switch from '@mui/material/Switch';
import reqProvider from '../../util/requests/reqProvider';
import Ingredients from './Ingredients';

interface DepositFee {
  depositFeePrice: number;
  hasDepositFee: string;
  depositFeeCount: number;
}

interface Modifier {
  id: number | null;
  warehouseProductId: number | null;
  warehouseProductUnitId: string;
  grossPriceDifference: number | null;
  isActive: boolean;
}

interface UnitComponent {
  id: number | null;
  warehouseProductId: number | null;
  warehouseProductUnitId: string;
  quantity: number | null;
  modifiers: Modifier[];
}

interface PackagingUnit {
  id: number | null;
  unit: number;
  unitName: string;
  quantity: number;
  defaultPrice: number;
  bulk: boolean;
  depositFeeList: DepositFee[];
  hasRecipe: boolean | null;
  unitComponents: UnitComponent[];
  quickrActive: boolean;
  quickrGrossPrice: number;
  barcode: string | null;
}

interface Unit {
  id: number,
  name: string
}

const ProductPackaging = ({ mode }: { mode: string }) => {
  const dispatch = useDispatch();
  const packaging = useSelector((state: RootState) => state.product.packaging);
  const [selectableUnits, setSelectableUnits] = useState<Unit[]>([]);
  const [openIngredientIndex, setOpenIngredientIndex] = useState<number | null>(null);

  const handleClose = () => {
    setOpenIngredientIndex(null);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    const { name, value } = event.target;
    const updatedUnits = [...packaging.units];
    updatedUnits[index] = {
      ...updatedUnits[index],
      [name]: value,
    };
    dispatch(setPackaging({ units: updatedUnits }));
  };

  const handleDepositFeeChange = (feeType: string, checked: boolean, index: number) => {
    const updatedUnits = [...packaging.units];
    if (checked) {
      updatedUnits[index] = {
        ...updatedUnits[index],
        depositFeeList: [
          ...updatedUnits[index].depositFeeList,
          {
            depositFeePrice: null,
            hasDepositFee: feeType,
            depositFeeCount: 1,
          },
        ],
      };
    } else {
      updatedUnits[index] = {
        ...updatedUnits[index],
        depositFeeList: updatedUnits[index].depositFeeList.filter(
          (fee) => fee.hasDepositFee !== feeType,
        ),
      };
    }
    dispatch(setPackaging({ units: updatedUnits }));
  };

  const handleDepositFeeCountChange = (feeType: string, count: number, index: number) => {
    const updatedUnits = [...packaging.units];
    updatedUnits[index] = {
      ...updatedUnits[index],
      depositFeeList: updatedUnits[index].depositFeeList.map(fee =>
        fee.hasDepositFee === feeType
          ? { ...fee, depositFeeCount: count }
          : fee,
      ),
    };
    dispatch(setPackaging({ units: updatedUnits }));
  };

  const addNewUnit = () => {
    const newUnit: PackagingUnit = {
      id: null,
      unit: 0,
      unitName: '',
      quantity: 0,
      defaultPrice: 0,
      bulk: false,
      depositFeeList: [],
      hasRecipe: null,
      unitComponents: [],
      quickrActive: false,
      quickrGrossPrice: 0,
      barcode: null,
    };

    dispatch(setPackaging({ units: [...packaging.units, newUnit] }));
  };

  useEffect(() => {
    reqProvider.get('/product/unit')
      .then(response => {
        setSelectableUnits(response.data.content);
      });
  }, []);

  useEffect(() => {
    if (mode !== 'update' && (!packaging.units || packaging.units.length === 0)) {
      addNewUnit();
    }
  }, [mode]);

  return (
    <>
    <Box sx={{
      display: 'flex',
      gap: 2,
      marginLeft: { xs: '0', sm: '160px' },
      marginTop: { xs: '0', sm: '-55px' },
      marginBottom: { xs: '20px', sm: '40px' },
      justifyContent: { xs: 'flex-start', sm: 'flex-start' }
    }}>
      <Button
        variant="contained"
        onClick={addNewUnit}
        sx={{
          mt: 2,
          minWidth: 'unset',
          width: 40,
          height: 40,
          borderRadius: '50%',
          p: 0,
        }}
      >
        <AddIcon />
      </Button>
      <Button
        variant="text"
        onClick={() => dispatch(setPackaging({ units: [] }))}
        sx={{
          mt: 2,
          p: 0,
          textTransform: 'none',
        }}
      >
        <DeleteIcon />
        Összes törlése
      </Button>
    </Box>

    <Box sx={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 2,
    }}>
      {packaging.units.map((unit, index) => (
        <Box key={index} sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 2,
          width: { xs: '100%', sm: '45%', md: '30%', lg: '400px' },
          backgroundColor: '#F9EAEF',
          borderRadius: '10px',
          position: 'relative',
        }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              sx={{ width: '100%' }}
              name="unit"
              label="Mértékegység"
              value={selectableUnits.find(elem => elem.id === unit.unit)?.name}
              onChange={(e) => handleInputChange(e, index)}
              size="small"
              select
            >
              {selectableUnits.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              sx={{ width: '100%' }}
              name="quantity"
              label="Mennyiség"
              value={unit.quantity || ''}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                if (value === '' || parseInt(value) >= 0) {
                  handleInputChange(e, index);
                }
              }}
              size="small"
              type="text"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            />
          </Box>
          <TextField
            name="defaultPrice"
            label="Alapértelmezett bruttó ár"
            value={unit.defaultPrice || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              if (value === '' || parseInt(value) >= 0) {
                handleInputChange(e, index);
              }
            }}
            size="small"
            type="text"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
          <Button variant="text" startIcon={<ControlPoint />} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                  onClick={() => {
                    setOpenIngredientIndex(index);
                  }}
          >
            Összetevő(k) hozzáadása
          </Button>
          <FormControlLabel
            control={
              <Switch
                name="bulk"
                checked={unit.bulk}
                onChange={
                  (e) => {
                    const updatedUnits = [...packaging.units];
                    updatedUnits[index] = {
                      ...updatedUnits[index],
                      bulk: e.target.checked,
                    };
                    dispatch(setPackaging({ units: updatedUnits }));
                  }
                }
              />
            }
            label="Kimért termék"
          />
          {!unit.bulk && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>Visszaváltható palack</Typography>
                <Tooltip placement={'right'} title="A betétdíj a beállított árhoz hozzáadódik.">
                  <HelpOutline sx={{ fontSize: 20, color: 'primary.main' }} />
                </Tooltip>
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    name="ONE_WAY"
                    checked={unit.depositFeeList.some((fee) => fee.hasDepositFee === 'ONE_WAY')}
                    onChange={(e, checked) => handleDepositFeeChange('ONE_WAY', checked, index)}
                  />
                }
                label="Egyutas palack (50 Ft)"
              />
              {unit.depositFeeList.some((fee) => fee.hasDepositFee === 'ONE_WAY') && (
                <TextField
                  name="depositFeeCount"
                  label="Db"
                  size="small"
                  type="text"
                  sx={{ width: '20%' }}
                  value={unit.depositFeeList.find(fee => fee.hasDepositFee === 'ONE_WAY')?.depositFeeCount || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    if (value === '' || parseInt(value) >= 0) {
                      handleDepositFeeCountChange('ONE_WAY', Number(value), index);
                    }
                  }}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    name="MULTI_WAY"
                    checked={unit.depositFeeList.some((fee) => fee.hasDepositFee === 'MULTI_WAY')}
                    onChange={(e, checked) => handleDepositFeeChange('MULTI_WAY', checked, index)}
                  />
                }
                label="Többutas palack"
              />
              {unit.depositFeeList.some((fee) => fee.hasDepositFee === 'MULTI_WAY') && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    name="depositFeeCount"
                    label="Db"
                    size="small"
                    type="text"
                    sx={{ width: '20%' }}
                    value={unit.depositFeeList.find(fee => fee.hasDepositFee === 'MULTI_WAY')?.depositFeeCount || ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      if (value === '' || parseInt(value) >= 0) {
                        handleDepositFeeCountChange('MULTI_WAY', Number(value), index);
                      }
                    }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  />
                  <TextField
                    name="depositFeePrice"
                    label="Ft"
                    size="small"
                    type="text"
                    sx={{ width: '20%' }}
                    value={unit.depositFeeList.find(fee => fee.hasDepositFee === 'MULTI_WAY')?.depositFeePrice || ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      if (value === '' || parseInt(value) >= 0) {
                        const updatedUnits = [...packaging.units];
                        updatedUnits[index] = {
                          ...updatedUnits[index],
                          depositFeeList: updatedUnits[index].depositFeeList.map(fee =>
                            fee.hasDepositFee === 'MULTI_WAY'
                              ? { ...fee, depositFeePrice: Number(value) }
                              : fee,
                          ),
                        };
                        dispatch(setPackaging({ units: updatedUnits }));
                      }
                    }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  />
                </Box>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    name="REVO_CUP"
                    checked={unit.depositFeeList.some((fee) => fee.hasDepositFee === 'REVO_CUP')}
                    onChange={(e, checked) => handleDepositFeeChange('REVO_CUP', checked, index)}
                  />
                }
                label="Revo Cup"
              />
              {unit.depositFeeList.some((fee) => fee.hasDepositFee === 'REVO_CUP') && (
                <TextField
                  name="depositFeeCount"
                  label="Db"
                  size="small"
                  type="text"
                  sx={{ width: '20%' }}
                  value={unit.depositFeeList.find(fee => fee.hasDepositFee === 'REVO_CUP')?.depositFeeCount || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    if (value === '' || parseInt(value) >= 0) {
                      handleDepositFeeCountChange('REVO_CUP', Number(value), index);
                    }
                  }}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                />
              )}
            </>
          )}
          <Button
            onClick={() => {
              const updatedUnits = packaging.units.filter((_, i) => i !== index);
              dispatch(setPackaging({ units: updatedUnits }));
            }}
            sx={{
              minWidth: 'unset',
              p: 0,
            }}
          >
            <DeleteIcon />
          </Button>
          {openIngredientIndex === index && (
            <Ingredients 
              open={openIngredientIndex === index} 
              onClose={handleClose} 
              unitComponents={unit.unitComponents}
              onUpdate={(updatedComponents) => {
                const updatedUnits = [...packaging.units];
                updatedUnits[index] = {
                  ...updatedUnits[index],
                  unitComponents: updatedComponents
                };
                dispatch(setPackaging({ units: updatedUnits }));
              }}
            />
          )}
        </Box>
    ))}
    </Box>
</>
)
  ;
};

export default ProductPackaging;
